import React, { useCallback, useRef } from "react";
import { HiOutlineSparkles } from "react-icons/hi";
import FloatingPopover, {
  FLOATING_POPOVER_MODE,
  FLOATING_POPOVER_VARIANT,
} from "components/common/FloatingPopover";
import {
  resolveAiAnomalyResultState,
  resolveAiCheckBadge,
  resolveAiOverallUiState,
  formatReadinessAsPercent,
  resolveAiTableScoreLabel,
  resolveAiTableVisualState,
  resolveReadinessScoreFromStatus,
  stripAiMarkdown,
  truncateAiText,
} from "../utils/timesheetAiUtils";

const ICON_STYLES = {
  red: "border-red-200 bg-red-50 text-red-500 hover:bg-red-100",
  amber:
    "border-amber-200 bg-amber-50 text-amber-600 hover:bg-amber-100 hover:shadow-sm",
  emerald:
    "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 hover:shadow-sm",
  blue:
    "border-blue-200 bg-[#ECF0FD] text-[#4B6CB7] hover:bg-blue-50 hover:shadow-sm",
};

const LABEL_TONE_CLASS = {
  red: "text-red-600",
  amber: "text-amber-700",
  emerald: "text-emerald-700",
  gray: "text-gray-500",
};

const resolveIconTone = (status) => {
  const visual = resolveAiTableVisualState(status);
  return { tone: visual.tone, flagged: visual.flagged };
};

const resolvePresentation = (status) => {
  if (!status) return null;

  if (status.failed) {
    return resolveAiAnomalyResultState({
      failed: true,
      failMessage: status.failMessage,
    });
  }

  if (status.headline || status.checkSummary || status.ready_to_approve != null) {
    return resolveAiOverallUiState({
      overall: {
        headline: status.headline,
        ready_to_approve: status.ready_to_approve,
        readiness_score: status.readiness_score,
        risk_level: status.risk_level,
        reject_probability: status.reject_probability,
      },
      checkSummary: status.checkSummary,
      anomaly_score: status.anomaly_score,
    });
  }

  return resolveAiAnomalyResultState({
    anomaly_score: status.anomaly_score,
  });
};

const formatIssueSummary = (issue) => {
  const summary = stripAiMarkdown(issue?.summary || issue?.label || "");
  const who = issue?.participant_label ? `${issue.participant_label}: ` : "";
  return truncateAiText(`${who}${summary}`, 180);
};

const AiTableScoreStack = ({ scorePercent, label, tone, fixCount = 0 }) => (
  <div className="flex min-w-[2.25rem] flex-col items-start leading-none">
    <span className="text-[11px] font-bold tabular-nums text-[#0F172A]">
      {scorePercent || "—%"}
    </span>
    <span
      className={`mt-0.5 text-[10px] font-semibold ${
        LABEL_TONE_CLASS[tone] || LABEL_TONE_CLASS.gray
      }`}
    >
      {label}
    </span>
    {fixCount > 0 ? (
      <span className="mt-0.5 text-[9px] font-semibold text-amber-700">
        {fixCount} to fix
      </span>
    ) : null}
  </div>
);

const resolvePopoverFooterCopy = ({ topIssues, totalFixCount }) => {
  if (totalFixCount > topIssues.length) {
    return `Showing ${topIssues.length} of ${totalFixCount} items to fix — click for full report`;
  }
  if (topIssues.length > 0) {
    return "View full report for all fixes and details";
  }
  return "Click to view full AI report";
};

const AiPopoverContent = ({ status, onViewReport }) => {
  if (!status) return null;

  const tableScore = resolveAiTableScoreLabel(status);

  if (status.failed) {
    return (
      <div className="max-w-[400px] whitespace-normal break-words px-3.5 py-3 text-left">
        <p className="text-xs font-semibold text-red-600">Analysis unavailable</p>
        <p className="mt-1.5 text-[11px] leading-relaxed text-gray-700">
          {stripAiMarkdown(status.failMessage) || "Could not complete AI check."}
        </p>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewReport?.();
          }}
          className="mt-2.5 text-left text-[11px] font-semibold text-[#4B6CB7] hover:underline"
        >
          Click to view details
        </button>
      </div>
    );
  }

  const presentation = resolvePresentation(status);
  const { tone } = resolveIconTone(status);
  const summary = status.checkSummary;
  const displayLabel =
    tableScore.label === "Passed"
      ? summary?.review > 0
        ? `Passed · ${summary.review} to fix`
        : "Ready to approve"
      : tableScore.label === "Critical"
        ? "Critical issues"
        : tableScore.label === "Review"
          ? "Needs review"
          : presentation?.label || "AI analysed";
  const headline = stripAiMarkdown(
    status.headline || presentation?.description || ""
  );
  const checkLine =
    summary && summary.total > 0
      ? `${summary.pass}/${summary.total} passed${
          summary.critical > 0 ? ` · ${summary.critical} critical` : ""
        }${
          summary.review > 0
            ? ` · ${summary.review} to ${tableScore.label === "Passed" ? "fix" : "review"}`
            : ""
        }`
      : null;
  const topIssues = (status.topIssues || []).slice(0, 3);
  const totalFixCount = Math.max(
    Number(summary?.review) || 0,
    topIssues.length
  );
  const footerCopy = resolvePopoverFooterCopy({ topIssues, totalFixCount });

  return (
    <div className="max-w-[400px] whitespace-normal break-words px-3.5 py-3 text-left">
      <div className="flex items-center gap-1.5">
        <HiOutlineSparkles
          className={`h-3.5 w-3.5 shrink-0 ${
            tone === "red"
              ? "text-red-500"
              : tone === "emerald"
                ? "text-emerald-600"
                : tone === "amber"
                  ? "text-amber-500"
                  : "text-[#4B6CB7]"
          }`}
        />
        <p
          className={`text-xs font-semibold ${
            tone === "red"
              ? "text-red-700"
              : tone === "emerald"
                ? "text-emerald-800"
                : tone === "amber"
                  ? "text-amber-800"
                  : "text-[#02397A]"
          }`}
        >
          {displayLabel}
        </p>
      </div>

      {headline && topIssues.length === 0 ? (
        <p className="mt-1.5 text-[11px] leading-relaxed text-gray-700">
          {truncateAiText(headline, 160)}
        </p>
      ) : null}

      {checkLine ? (
        <p className="mt-1.5 rounded-md bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-700">
          {checkLine}
        </p>
      ) : null}

      {topIssues.length > 0 ? (
        <ul className="mt-2 space-y-1.5 border-t border-gray-100 pt-2">
          {topIssues.map((issue) => (
            <li key={`${issue.id}-${issue.label}`} className="text-[11px] leading-snug">
              <span
                className={`mr-1 inline-flex rounded px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                  resolveAiCheckBadge(issue).tone === "red"
                    ? "bg-red-50 text-red-700"
                    : "bg-amber-50 text-amber-800"
                }`}
              >
                {issue.badge || resolveAiCheckBadge(issue).label}
              </span>
              <span className="text-gray-800">{formatIssueSummary(issue)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onViewReport?.();
        }}
        className="mt-2.5 text-left text-[11px] font-semibold text-[#4B6CB7] hover:underline"
      >
        {footerCopy}
      </button>
    </div>
  );
};

const TimesheetAiStatusIcon = ({ timesheetId, status, onOpenReport }) => {
  const logoRef = useRef(null);

  const handleViewReport = useCallback(() => {
    onOpenReport?.(timesheetId, status);
  }, [onOpenReport, status, timesheetId]);

  if (!status) return null;

  const { tone } = resolveIconTone(status);
  const tableScore = resolveAiTableScoreLabel(status);
  const iconTone = status.failed ? "red" : tone;
  const scorePercent =
    formatReadinessAsPercent(resolveReadinessScoreFromStatus(status)) || "—%";

  if (!status.failed && !status.hasReport) return null;

  return (
    <FloatingPopover
      mode={FLOATING_POPOVER_MODE.HOVER}
      variant={FLOATING_POPOVER_VARIANT.BRAND}
      anchorRef={logoRef}
      maxWidth={420}
      zIndex={10075}
      content={({ close }) => (
        <AiPopoverContent
          status={status}
          onViewReport={() => {
            close();
            handleViewReport();
          }}
        />
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleViewReport();
        }}
        className="inline-flex items-center gap-1.5 rounded-lg py-0.5 pr-1 transition-opacity hover:opacity-90"
        aria-label={
          status.failed
            ? "View unavailable AI analysis"
            : `View AI report — ${scorePercent} ${tableScore.label}`
        }
      >
        <span
          ref={logoRef}
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-all ${
            ICON_STYLES[iconTone] || ICON_STYLES.blue
          }`}
        >
          <HiOutlineSparkles className="h-4 w-4" />
        </span>
        <AiTableScoreStack
          scorePercent={scorePercent}
          label={tableScore.label}
          tone={tableScore.tone}
          fixCount={tableScore.fixCount}
        />
      </button>
    </FloatingPopover>
  );
};

export default TimesheetAiStatusIcon;
